<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { useSuperadmin } from '@/composables/useSuperadmin'
import { useSnackbar } from '@/composables/useSnackbar'

// Conversations — read-only archive of every Messenger/WhatsApp chat
// between customers and the bot. Superadmin only.
//
// Two-pane layout:
//   left  = list of distinct (channel, psid) threads, newest first
//           with optional search by name / client_id / phone / text
//   right = full ordered message history for the selected thread,
//           customer bubbles left + bot bubbles right
//
// Data sources:
//   GET /api/shipment/admin/chat/conversations?q=&limit=&offset=
//   GET /api/shipment/admin/chat/thread?channel=&psid=

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Customers',
    pageTitle: 'Conversations',
    requiresSuperadmin: true,
    breadcrumb: [
      { title: 'Home', to: '/' },
      { title: 'Shipment', to: '/shipment' },
      { title: 'Conversations', active: true },
    ],
  },
})

const router = useRouter()
const route  = useRoute()
const { isSuperadmin } = useSuperadmin()
const { error: snackError } = useSnackbar()

// Soft-guard the UI even though the backend enforces role:superadmin.
// Without this, a non-superadmin who manually navigates here gets a
// blank pane + 403 — much friendlier to show a clear "denied" card.
if (!isSuperadmin.value) {
  // Don't redirect (could trap users in a redirect loop if a stale role
  // cache disagrees with the server); just render the denied state below.
}

// --- conversation list (left pane) ---
// Channel is hard-locked to 'whatsapp' (see comment in template). Server
// also filters server-side as defense in depth.
const CHANNEL = 'whatsapp'
const conversations = ref([])
const listLoading   = ref(false)
const search        = ref('')
const reachedEnd    = ref(false)
const PAGE_SIZE     = 50

// --- selected thread (right pane) ---
const selected       = ref(null)  // { channel, psid }
const threadMessages = ref([])
const threadContact  = ref(null)
const threadLoading  = ref(false)

const channelMeta = {
  messenger: { icon: 'tabler-brand-messenger', color: 'info'      },
  whatsapp:  { icon: 'tabler-brand-whatsapp',  color: 'success'   },
  web:       { icon: 'tabler-world',           color: 'secondary' },
}
const channelIcon  = ch => channelMeta[ch]?.icon  ?? 'tabler-message-circle'
const channelColor = ch => channelMeta[ch]?.color ?? 'default'

// FR locale formatting — matches the rest of the warehouse UI.
const fmtDateTime = iso => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      year: '2-digit', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return iso }
}
const fmtTime = iso => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso))
  } catch { return '' }
}
const fmtDay = iso => {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(new Date(iso))
  } catch { return '' }
}

const conversationLabel = c => {
  if (c.contact?.name) {
    const cid = c.contact.client_id ? ` (${c.contact.client_id})` : ''
    return `${c.contact.name}${cid}`
  }
  if (c.display_name) return c.display_name
  return c.psid
}

async function loadConversations({ append = false } = {}) {
  if (!isSuperadmin.value) return
  listLoading.value = true
  try {
    const offset = append ? conversations.value.length : 0
    const { data } = await axios.get('shipment/admin/chat/conversations', {
      params: { q: search.value, channel: CHANNEL, limit: PAGE_SIZE, offset },
    })
    const rows = data?.data ?? []
    conversations.value = append
      ? [...conversations.value, ...rows]
      : rows
    reachedEnd.value = rows.length < PAGE_SIZE
  } catch (e) {
    if (e?.response?.status === 403) {
      snackError('Accès refusé — superadmin requis.')
    } else {
      snackError(e?.response?.data?.message || 'Erreur lors du chargement.')
    }
  } finally {
    listLoading.value = false
  }
}

async function loadThread(channel, psid) {
  threadLoading.value  = true
  threadMessages.value = []
  threadContact.value  = null
  try {
    const { data } = await axios.get('shipment/admin/chat/thread', {
      params: { channel, psid },
    })
    threadMessages.value = data?.messages ?? []
    threadContact.value  = data?.contact ?? null
    await nextTick()
    // Auto-scroll to bottom so the most recent message is visible.
    const pane = document.querySelector('.chat-thread-pane')
    if (pane) pane.scrollTop = pane.scrollHeight
  } catch (e) {
    snackError(e?.response?.data?.message || 'Erreur lors du chargement du fil.')
  } finally {
    threadLoading.value = false
  }
}

function selectConversation(c) {
  selected.value = { channel: c.channel, psid: c.psid }
  router.replace({ query: { ...route.query, channel: c.channel, psid: c.psid } })
  loadThread(c.channel, c.psid)
}

// Fallback display name for the thread header when no Contact is linked
// yet — pulled from messages.display_name (Meta sender profile name) or
// from the matching row in the conversations list.
const threadDisplayName = computed(() => {
  const fromMsgs = threadMessages.value.find(m => m.display_name)?.display_name
  if (fromMsgs) return fromMsgs
  if (!selected.value) return null
  const conv = conversations.value.find(
    c => c.channel === selected.value.channel && c.psid === selected.value.psid,
  )
  return conv?.display_name ?? null
})

// Group messages by calendar day so we can insert date separators.
const groupedMessages = computed(() => {
  const out = []
  let lastDay = null
  for (const m of threadMessages.value) {
    const day = (m.sent_at || m.created_at || '').slice(0, 10)
    if (day !== lastDay) {
      out.push({ type: 'day', key: `d-${day}`, day })
      lastDay = day
    }
    out.push({ type: 'msg', key: `m-${m.id}`, msg: m })
  }
  return out
})

// Debounce search — keypress every 250ms is plenty.
let searchTimer = null
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => loadConversations(), 250)
})

onMounted(async () => {
  if (!isSuperadmin.value) return
  await loadConversations()
  // Deep-link support: /shipment/admin/conversations?channel=whatsapp&psid=261...
  const ch  = route.query.channel
  const pid = route.query.psid
  if (ch && pid) {
    selected.value = { channel: ch, psid: pid }
    loadThread(ch, pid)
  }
})
</script>

<template>
  <div>
    <!-- Denied state — when an operator without superadmin lands here. -->
    <VCard v-if="!isSuperadmin" class="pa-8 text-center">
      <VIcon icon="tabler-lock" size="48" color="warning" class="mb-2" />
      <h3 class="text-h6 mb-1">Accès restreint</h3>
      <p class="text-body-2 text-medium-emphasis">
        Cette page est réservée au superadmin.
      </p>
    </VCard>

    <VRow v-else no-gutters class="chat-page">
      <!-- LEFT: conversation list -->
      <VCol cols="12" md="4" lg="3" class="chat-list-col">
        <VCard class="h-100 d-flex flex-column" flat tile>
          <VCardText class="pa-3 flex-shrink-0">
            <VTextField
              v-model="search"
              density="compact"
              prepend-inner-icon="tabler-search"
              placeholder="Rechercher (nom, ID, téléphone, texte)"
              hide-details
              clearable
            />
            <!-- WhatsApp-only by design: Messenger conversations are already
                 visible in Meta Business Suite Inbox under each Page, so we
                 don't duplicate them here. WhatsApp Cloud API has no
                 Meta-side inbox, so Conversations is the only place to read
                 the bot's WA history. -->
            <div class="d-flex align-center gap-2 mt-2">
              <VChip size="small" variant="flat" color="success">
                <VIcon icon="tabler-brand-whatsapp" size="14" start />
                WhatsApp uniquement
              </VChip>
            </div>
          </VCardText>

          <VDivider />

          <div class="chat-list-pane flex-grow-1 overflow-y-auto">
            <VList density="compact" nav>
              <VListItem
                v-for="c in conversations"
                :key="`${c.channel}:${c.psid}`"
                :active="selected && selected.channel === c.channel && selected.psid === c.psid"
                @click="selectConversation(c)"
              >
                <template #prepend>
                  <VAvatar :color="channelColor(c.channel)" variant="tonal" size="36">
                    <VIcon :icon="channelIcon(c.channel)" />
                  </VAvatar>
                </template>
                <VListItemTitle class="text-truncate">
                  {{ conversationLabel(c) }}
                </VListItemTitle>
                <VListItemSubtitle class="text-truncate">
                  {{ c.contact?.phone || c.psid }} · {{ c.message_count }} msg
                </VListItemSubtitle>
                <template #append>
                  <span class="text-caption text-medium-emphasis">
                    {{ fmtDateTime(c.last_message_at) }}
                  </span>
                </template>
              </VListItem>

              <VListItem v-if="listLoading" class="text-center py-4">
                <VProgressCircular indeterminate size="20" />
              </VListItem>
              <VListItem v-else-if="!conversations.length" class="text-center py-4 text-medium-emphasis">
                Aucune conversation
              </VListItem>
              <VListItem v-else-if="!reachedEnd" class="text-center py-2">
                <VBtn variant="text" size="small" @click="loadConversations({ append: true })">
                  Charger plus
                </VBtn>
              </VListItem>
            </VList>
          </div>
        </VCard>
      </VCol>

      <!-- RIGHT: thread -->
      <VCol cols="12" md="8" lg="9">
        <VCard class="h-100 d-flex flex-column" flat tile>
          <!-- Header -->
          <VCardText v-if="selected" class="pa-3 flex-shrink-0 d-flex align-center gap-3">
            <VAvatar :color="channelColor(selected.channel)" variant="tonal" size="40">
              <VIcon :icon="channelIcon(selected.channel)" />
            </VAvatar>
            <div class="flex-grow-1">
              <div class="font-weight-medium">
                <template v-if="threadContact">
                  {{ threadContact.name }}
                  <span v-if="threadContact.client_id" class="text-caption text-medium-emphasis ml-1">
                    ({{ threadContact.client_id }})
                  </span>
                </template>
                <template v-else-if="threadDisplayName">
                  {{ threadDisplayName }}
                  <VChip size="x-small" variant="tonal" color="warning" class="ml-2">
                    Non identifié
                  </VChip>
                </template>
                <template v-else>
                  {{ selected.psid }}
                </template>
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ selected.channel }} · {{ selected.psid }}
                <template v-if="threadContact?.phone"> · {{ threadContact.phone }}</template>
              </div>
            </div>
            <VBtn
              v-if="threadContact"
              :to="{ name: 'shipment-customers', query: { id: threadContact.id } }"
              variant="tonal"
              size="small"
              prepend-icon="tabler-user"
            >
              Profil client
            </VBtn>
          </VCardText>
          <VCardText v-else class="pa-3 flex-shrink-0 text-medium-emphasis">
            Sélectionnez une conversation à gauche.
          </VCardText>

          <VDivider />

          <!-- Messages -->
          <div class="chat-thread-pane flex-grow-1 overflow-y-auto pa-4">
            <div v-if="threadLoading" class="text-center py-8">
              <VProgressCircular indeterminate />
            </div>
            <template v-else-if="selected">
              <template v-for="item in groupedMessages" :key="item.key">
                <div v-if="item.type === 'day'" class="text-center my-3">
                  <VChip size="x-small" variant="tonal" color="default">
                    {{ fmtDay(item.day) }}
                  </VChip>
                </div>
                <div
                  v-else
                  class="d-flex mb-2"
                  :class="item.msg.direction === 'in' ? 'justify-start' : 'justify-end'"
                >
                  <div
                    class="chat-bubble pa-3"
                    :class="item.msg.direction === 'in' ? 'bubble-in' : 'bubble-out'"
                  >
                    <div v-if="item.msg.message_type === 'image' && item.msg.media_url">
                      <a :href="item.msg.media_url" target="_blank" rel="noopener">
                        <img :src="item.msg.media_url" class="chat-image" alt="image" />
                      </a>
                      <div v-if="item.msg.text" class="mt-1 text-body-2">{{ item.msg.text }}</div>
                    </div>
                    <div v-else-if="item.msg.message_type === 'postback'" class="text-body-2">
                      <VIcon icon="tabler-hand-click" size="14" class="mr-1" />
                      <code>{{ item.msg.text }}</code>
                    </div>
                    <div v-else-if="item.msg.message_type === 'quick_reply'" class="text-body-2">
                      <VIcon icon="tabler-bolt" size="14" class="mr-1" />
                      {{ item.msg.text }}
                    </div>
                    <div v-else class="text-body-2" style="white-space: pre-wrap">{{ item.msg.text }}</div>
                    <div class="text-caption text-medium-emphasis text-right mt-1">
                      {{ fmtTime(item.msg.sent_at || item.msg.created_at) }}
                    </div>
                  </div>
                </div>
              </template>
              <div v-if="!groupedMessages.length" class="text-center text-medium-emphasis py-8">
                Aucun message dans ce fil.
              </div>
            </template>
          </div>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
/* Use a bigger top-offset so the chat pane never collides with the page
   footer ("© 2026 GoDana Shipping — All Rights Reserved"). The default
   layout adds ~64px header + ~52px footer + breadcrumbs/padding, so
   reserve ~220px. min-height keeps the pane usable on short windows. */
.chat-page {
  height: calc(100vh - 220px);
  min-height: 480px;
  margin-bottom: 16px;
}
.chat-list-col {
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}
.chat-list-pane,
.chat-thread-pane {
  min-height: 0;
}
.chat-thread-pane {
  /* Subtle dotted background — works in both light and dark themes. */
  background-image: radial-gradient(rgba(var(--v-theme-on-surface), 0.05) 1px, transparent 1px);
  background-size: 18px 18px;
}
.chat-bubble {
  max-width: 70%;
  border-radius: 12px;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}
.bubble-in {
  /* Customer side — explicit grey-100 / grey-800 instead of theme tokens
     so it reads correctly in BOTH light and dark mode. Theme tokens like
     `surface` are too close to the page background in dark mode and the
     bubble disappears (the bug from last screenshot). */
  background-color: rgb(245, 245, 245);
  color: rgba(0, 0, 0, 0.87);
}
.v-theme--dark .bubble-in {
  background-color: rgb(55, 60, 76);
  color: rgba(255, 255, 255, 0.92);
}
.bubble-out {
  /* Bot side — primary brand color. on-primary token handles contrast. */
  background-color: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}
.bubble-in .text-medium-emphasis {
  /* Make the timestamp readable on both light and dark bubble fills. */
  color: rgba(0, 0, 0, 0.55) !important;
}
.v-theme--dark .bubble-in .text-medium-emphasis {
  color: rgba(255, 255, 255, 0.6) !important;
}
.bubble-out .text-medium-emphasis {
  color: rgba(var(--v-theme-on-primary), 0.75) !important;
}
.chat-image {
  max-width: 260px;
  max-height: 260px;
  border-radius: 6px;
  display: block;
}
</style>
